import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
// ============================================
// Guidance — PDF #11 Rehberlik
// ============================================
export const GuidanceMemberRoleSchema = z.enum(['student', 'teacher']);
export const GuidanceContentKindSchema = z.enum([
    'video',
    'document',
    'link',
    'google_drive',
    'exam_ref',
    'worksheet_ref',
    'weekly_plan',
]);
// ---- Group ----
export const GuidanceGroupSchema = z.object({
    id: IdSchema,
    name: z.string(),
    description: z.string().nullable(),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    // hydrated counts (server fills)
    memberCount: z.number().int().optional(),
    studentCount: z.number().int().optional(),
    teacherCount: z.number().int().optional(),
    contentCount: z.number().int().optional(),
});
export const CreateGuidanceGroupSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
});
export const UpdateGuidanceGroupSchema = CreateGuidanceGroupSchema.partial();
export const GuidanceGroupQuerySchema = PaginationParamsSchema.extend({
    search: z.string().optional(),
});
// ---- Member ----
export const GuidanceGroupMemberSchema = z.object({
    id: IdSchema,
    groupId: IdSchema,
    userId: IdSchema,
    role: GuidanceMemberRoleSchema,
    createdAt: DateTimeSchema,
    // hydrated
    userName: z.string().nullable().optional(),
    userEmail: z.string().nullable().optional(),
});
export const AddGuidanceMembersSchema = z.object({
    role: GuidanceMemberRoleSchema,
    userIds: z.array(IdSchema).min(1).max(200),
});
// ---- Content ----
// Haftalik program body sema (kind=weekly_plan icin):
// subjectId, gunler ve hedef soru sayilari. Boyle bir yapi musterinin Excel
// gorseliyle birebir uyumlu.
export const WeeklyPlanRowSchema = z.object({
    subjectId: IdSchema,
    weeklyTotalQuestions: z.number().int().min(0).max(100_000).optional(),
    // Kitap ödevi ise kitap adı ve sayfalar gibi serbest metin notu.
    note: z.string().max(500).optional(),
    // Ogretmenin sectigi gunler — name + hedef. Max 7 gun (bir hafta).
    days: z
        .array(z.object({
        // 1=Pazartesi .. 7=Pazar (ISO)
        dayOfWeek: z.number().int().min(1).max(7),
        targetQuestionCount: z.number().int().min(0).max(10_000),
    }))
        .max(7),
});
export const WeeklyPlanCompletionAttachmentSchema = z.object({
    url: z.string().min(1).max(5_000_000),
    kind: z.enum(['image', 'video', 'file']),
    mimeType: z.string().max(120).optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
});
export const WeeklyPlanDayCompletionSchema = z.object({
    completedAt: DateTimeSchema,
    attachments: z.array(WeeklyPlanCompletionAttachmentSchema).max(10).optional(),
});
/** studentUserId → { "rowIndex:dayOfWeek" → completion } */
export const WeeklyPlanStudentCompletionsSchema = z.record(z.string(), WeeklyPlanDayCompletionSchema);
export const WeeklyPlanBodySchema = z.object({
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
    courseId: IdSchema.optional(),
    studentIds: z.array(IdSchema).max(500).optional(),
    rows: z.array(WeeklyPlanRowSchema).max(50),
    studentCompletions: z.record(z.string(), WeeklyPlanStudentCompletionsSchema).optional(),
});
export const CompleteWeeklyPlanDaySchema = z.object({
    rowIndex: z.number().int().min(0).max(49),
    dayOfWeek: z.number().int().min(1).max(7),
    attachments: z.array(WeeklyPlanCompletionAttachmentSchema).max(10).optional(),
});
export const GuidanceContentSchema = z.object({
    id: IdSchema,
    groupId: IdSchema,
    kind: GuidanceContentKindSchema,
    title: z.string(),
    url: z.string().nullable(),
    refId: IdSchema.nullable(),
    body: z.any().nullable(),
    orderIndex: z.number().int(),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
// GuidanceContent.url navigable link (video/document/link kind). Sadece
// http(s) protokol kabul — data: ve javascript: protokolleri reddedilir
// (XSS riski). Inline ikili icerik HomeworkSubmissionAttachment akisinda
// data: URL ile gider, GuidanceContent'te degil.
const UrlString = HttpOrInternalFileUrlSchema;
export const CreateGuidanceContentSchema = z
    .object({
    kind: GuidanceContentKindSchema,
    title: z.string().min(1).max(300),
    url: UrlString.optional(),
    refId: IdSchema.optional(),
    body: z.any().optional(),
    orderIndex: z.coerce.number().int().min(0).optional(),
})
    .superRefine((v, ctx) => {
    if ((v.kind === 'video' ||
        v.kind === 'document' ||
        v.kind === 'link' ||
        v.kind === 'google_drive') &&
        !v.url) {
        ctx.addIssue({
            code: 'custom',
            path: ['url'],
            message: `url required for kind=${v.kind}`,
        });
    }
    if ((v.kind === 'exam_ref' || v.kind === 'worksheet_ref') && !v.refId) {
        ctx.addIssue({
            code: 'custom',
            path: ['refId'],
            message: `refId required for kind=${v.kind}`,
        });
    }
    if (v.kind === 'weekly_plan') {
        const parsed = WeeklyPlanBodySchema.safeParse(v.body);
        if (!parsed.success) {
            ctx.addIssue({
                code: 'custom',
                path: ['body'],
                message: 'body must match WeeklyPlanBody schema',
            });
        }
    }
    else if (v.body !== undefined && v.body !== null) {
        // Diger kind'larda body kullanilmamali; gelirse 8KB ile sinirla.
        const len = JSON.stringify(v.body).length;
        if (len > 8192) {
            ctx.addIssue({
                code: 'custom',
                path: ['body'],
                message: `body too large for kind=${v.kind} (max 8192 chars)`,
            });
        }
    }
});
export const UpdateGuidanceContentSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    url: UrlString.optional(),
    refId: IdSchema.optional(),
    body: z.any().optional(),
    orderIndex: z.coerce.number().int().min(0).optional(),
});
//# sourceMappingURL=guidance.js.map